import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorMissing, ensureSectionExpanded } from './tasks-help.shared';

/**
 * ?-Hilfe fuer den Checklisten-Editor einer Aufgabe.
 *
 * Die tragende Aussage: **ein Checklisten-Punkt ist die kleinste pruefbare
 * Einheit.** Er verlangt eine Antwort (Zahl, Auswahl, Text, Datum, Ja/Nein,
 * Foto), nicht nur einen Haken — und ueber Pflicht-Schalter und Bedingungen
 * wird daraus eine Voraussetzung fuers Erledigen. Ausgewertet wird das
 * AUSSCHLIESSLICH serverseitig; die Oberflaeche spiegelt die Regel nur.
 *
 * **Warum eigenstaendige Tour und kein Kapitel der Anlege-Tour:** Der
 * Checklisten-Editor ist eine geteilte Komponente und an ZWEI Stellen
 * eingebettet — im Anlege-/Bearbeiten-Sheet
 * (`task-create-edit-sheet.component.ts`) und im Routinen-Editor
 * (`routine-editor.component.html`), dort mit `[allowPhoto]="false"`, also
 * ohne den Feldtyp "Foto". Als Kapitel der Anlege-Tour waere die Erklaerung
 * aus dem Routinen-Editor heraus unerreichbar. Beide Einbettungen haben
 * bereits einen eigenen "?"-Knopf im Abschnittskopf, der genau diese Tour
 * startet; die Anlege-Tour verlinkt sie zusaetzlich als Folge-Aktion.
 *
 * **Warum fast alles `optional` + `skipIf` ist:** Ohne mindestens einen
 * angelegten Punkt existiert KEIN einziger Feld-Anker — `cl-item` bis
 * `cl-conditions` haengen alle am ersten Punkt bzw. dessen erster Bedingung
 * (siehe Anker-Block "TASKS — Checklisten-Editor" in `anchor.registry.ts`).
 * Die Tour legt bewusst KEINEN Beispielpunkt an: sie wuerde ungefragt in das
 * Formular des Nutzers schreiben. Uebrig bleiben in der leeren Checkliste die
 * beiden Rahmen-Schritte (Einstieg und "Punkt hinzufuegen") — genau die, die
 * ohne Bestand ueberhaupt etwas erklaeren koennen.
 *
 * Wie in der Anlege-Tour laufen alle Schritte mit `popoverOutsideDialog`
 * (`size="lg"`, ein Popover INNERHALB verdeckt genau das Feld, das es
 * erklaert) und `panelIndex: -1` (der Dialog ist kein Material-Accordion;
 * ohne den Wert liefe der Coordinator in den Legacy-Zweig und klickte jeden
 * Kopf an).
 *
 * Der Abschnitt "Checkliste" ist einklappbar und braucht
 * {@link ensureSectionExpanded}: `xds-section` versteckt den Body per
 * `[hidden]`, der Anker bleibt also im DOM und `skipIf` wuerde NICHT greifen —
 * der Schritt haenge stattdessen 8s im Sichtbarkeits-Guard.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

export const TASK_CHECKLIST_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskChecklistHelp,
  version: 1,
  title: 'TASK_CHECKLIST_HELP_TOUR_TITLE',
  description: 'TASK_CHECKLIST_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Start aus dem Hilfe-Hub: zur Uebersicht navigieren, dann "Aufgabe anlegen"
  // klicken. Aus einem bereits offenen Dialog heraus (Sheet ODER
  // Routinen-Editor) startet der ?-Knopf direkt, der Block wird uebersprungen.
  launchContext: {
    route: '/tasks/overview',
    opens: { openerAnchor: AnchorId.TasksCreateButton, settleMs: 400 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 110,
  category: 'feature-discovery',
  // Fuenf der sieben Schritte entfallen in einer leeren Checkliste. Ohne
  // Umnummerierung zaehlte das Popover "1 / 7 → 7 / 7".
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_CHECKLIST_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'cl-welcome',
          anchor: AnchorId.TaskSheetChecklist,
          spotlightAnchor: AnchorId.TaskSheetChecklist,
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.WELCOME.CONTENT',
          audioStepId: 'cl-welcome-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
          onBeforeShow: () => ensureSectionExpanded(AnchorId.TaskSheetChecklist),
        },
        {
          // Ab hier haengt alles am ERSTEN Punkt — ohne Bestand kein Anker.
          id: 'cl-item',
          anchor: AnchorId.TaskChecklistItem,
          spotlightAnchor: AnchorId.TaskChecklistItem,
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.ITEM.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.ITEM.CONTENT',
          audioStepId: 'cl-item-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskChecklistItem),
        },
        {
          // Kern-Step: sieben Antwortarten plus Pflicht-Schalter. Im
          // Routinen-Editor fehlt "Foto" (`[allowPhoto]="false"`) — der Text
          // bleibt trotzdem gueltig, weil er die Arten aufzaehlt, statt sie
          // durchzunummerieren.
          id: 'cl-fieldtype',
          anchor: AnchorId.TaskChecklistFieldType,
          spotlightAnchor: AnchorId.TaskChecklistFieldType,
          additionalSpotlightAnchors: [AnchorId.TaskChecklistRequired],
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.FIELD_TYPE.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.FIELD_TYPE.CONTENT',
          audioStepId: 'cl-fieldtype-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskChecklistFieldType),
        },
        {
          // Nur bei Feldtyp "Zahl" im DOM. `-v3` seit 10.10.2026 (Abweichungen stehen unter
          // „Nachweise“ im Reiter „Lage“ statt in der „Compliance-Auswertung“), bis zur Vertonung stumm.
          id: 'cl-number',
          anchor: AnchorId.TaskChecklistNumber,
          spotlightAnchor: AnchorId.TaskChecklistNumber,
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.NUMBER.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.NUMBER.CONTENT',
          audioStepId: 'cl-number-v3',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskChecklistNumber),
        },
        {
          // Nur bei Feldtyp "Auswahl" im DOM. Der Schluessel ist das, worauf
          // Bedingungen zeigen — nicht die Beschriftung.
          id: 'cl-options',
          anchor: AnchorId.TaskChecklistOptions,
          spotlightAnchor: AnchorId.TaskChecklistOptions,
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.OPTIONS.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.OPTIONS.CONTENT',
          audioStepId: 'cl-options-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskChecklistOptions),
        },
        {
          // Kern-Step: "Wenn die Antwort … dann Nachweis erfordern oder
          // Erledigen blockieren". Definiert wird die Regel hier, ausgewertet
          // wird sie ausschliesslich serverseitig.
          id: 'cl-conditions',
          anchor: AnchorId.TaskChecklistConditions,
          spotlightAnchor: AnchorId.TaskChecklistConditions,
          additionalSpotlightAnchors: [AnchorId.TaskChecklistConditionEffect],
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.CONDITIONS.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.CONDITIONS.CONTENT',
          audioStepId: 'cl-conditions-v2',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => anchorMissing(AnchorId.TaskChecklistConditions),
        },
        {
          // Immer im DOM — auch in der leeren Checkliste. Hoechstens 100
          // Punkte, und die Reihenfolge ist die Arbeitsreihenfolge.
          id: 'cl-add',
          anchor: AnchorId.TaskChecklistAdd,
          spotlightAnchor: AnchorId.TaskChecklistAdd,
          title: 'TUTORIAL.TASK_CHECKLIST_EDITOR.ADD.TITLE',
          content: 'TUTORIAL.TASK_CHECKLIST_EDITOR.ADD.CONTENT',
          audioStepId: 'cl-add-v2',
          placement: 'top',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
        },
      ],
    },
  ],
};
